import { ButtonMenu } from '@/components/Elements/ButtonMenu';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { BookOpenIcon, PencilAltIcon } from '@heroicons/react/outline';
import RequestStatusPill from '@oncolens/oncolens-ui/lib/components/Collaboration/RequestStatusPill';
import { RequestDto } from '@oncolens/oncolens-ui/lib/dto/event/request';
import { getTestSelector } from '@oncolens/oncolens-ui/lib/utilities/tests';
import clsx from 'clsx';
import React, { Fragment } from 'react';
import { BiCollapse, BiExpand } from 'react-icons/bi';
import { FaEllipsisH, FaExclamation, FaSpinner } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { collaborationTemplateComponentExists } from '@/utils/templateUtils';
import { useGetRequests } from '../api/getRequests';
import { useGetTemplates } from '../api/getTemplates';
import { useGetWorkflows } from "@/features/requests/api/getWorkflows";

interface Action {
	label: string;
	svg: JSX.Element;
	onClick: (request: RequestDto) => void;
	show?: (request: RequestDto) => boolean;
}

function NoRecords() {
  return (
    <div className="flex-col items-center justify-center w-full px-4 py-2 mt-4 border-b border-gray-200 shadow sm:rounded-lg">
      <p className="font-medium text-md">You don&apos;t have any requests yet</p>
    </div>
  );
}

function Requests() {
  const navigate = useNavigate();
  const { isLoading, data } = useGetRequests({
    refetchOnWindowFocus: true,
  });
  const { data: templateList } = useGetTemplates();
  const { data: workflowList } = useGetWorkflows();
  const templates =
		templateList
		  ?.filter((template) => collaborationTemplateComponentExists(template.component))
		  .map((template) => ({
		    label: template.name,
		    onClick: () => navigate(`/newrequest/${template.id}`)
		  })) ?? [];
  const actions = [
    {
      label: 'View Request',
      svg: <BookOpenIcon className="w-5 h-5" />,
      onClick: (request: RequestDto) => navigate(`/requests/${request.id}/view`)
    },
    {
      label: 'Edit Request',
      svg: <PencilAltIcon className="w-5 h-5" />,
      onClick: (request: RequestDto) => {
        const workflow = workflowList?.find((workflow) => workflow.id === request.workflowId);
        const template = templateList?.find((template) => template.workflowId === workflow?.id);

        navigate(`/newrequest/${template?.id}?requestId=${request.id}`);
      },
      show: (request: RequestDto) => {
        return request.status !== 'Report Ready' && request.status !== 'Report Received'
      }
    }
    // {
    //   label: 'Download Report',
    //   svg: <CloudDownloadIcon className="w-5 h-5" />,
    //   onClick: (requestId: string) => window.alert(`Download Report for ${requestId}`)
    // }
  ] as Action[];

  return (
    <div className="pt-8 pb-48 sm:pt-16 sm:px-12">
      <div className="py-3 border-b">
        <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">My Requests</h1>
      </div>

      <div className="max-w-sm pt-4 mx-auto space-y-2 md:space-y-0 md:flex md:items-center md:justify-between md:max-w-full">
        <ButtonMenu
          className="items-center justify-center w-full md:inline-flex md:w-auto md:justify-start"
          size="sm"
          menuItems={templates}
        >
					New Request
        </ButtonMenu>
      </div>
      {data === undefined || data.length <= 0 ? (
        <NoRecords />
      ) : (
        data && (
          <>
            <div className="flex-col hidden pt-3 md:flex">
              <div className="-my-2 sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <div className="border-b border-gray-200 shadow sm:rounded-lg">
                    <table className="relative min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="relative w-16 py-3">
                            <span className="sr-only">Actions</span>
                          </th>
                          <th scope="col" className="relative w-4 py-3">
                            <span className="sr-only">Priority</span>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                          >
														Case
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                          >
														Submitted To
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                          >
														Status
                          </th>
                        </tr>
                      </thead>
                      <Transition
                        as="tbody"
                        show={isLoading}
                        className="absolute top-0 left-0 flex items-center justify-center w-full h-32 bg-white divide-y divide-gray-200"
                        enter="transition-opacity duration-200"
                        enterFrom="opacity-100"
                        enterTo="opacity-100"
                        leave="transition-opacity duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <FaSpinner className="w-6 h-6 duration-300 animate-spin" />
                      </Transition>
                      <Transition
                        as="tbody"
                        appear={true}
                        show={!isLoading}
                        className="bg-white divide-y divide-gray-200"
                        enter="transition-opacity duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        {data
                          ?.sort(
                            (a, b) =>
                              new Date(b.createdDate).valueOf() - new Date(a.createdDate).valueOf()
                          )
                          .map((request) => (
                            <RequestRow key={request.id} request={request} actions={actions} />
                          ))}
                      </Transition>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-col pt-6 space-y-5 md:hidden">
              {data
                ?.sort(
                  (a, b) => new Date(b.createdDate).valueOf() - new Date(a.createdDate).valueOf()
                )
                .map((request) => (
                  <MobileRequestRow key={request.id} request={request} actions={actions} />
                ))}
            </div>
          </>
        )
      )}
    </div>
  );
}

interface AccordionProps {
	title: string;
	children: React.ReactNode;
}

function ActionAccordion({ title, children }: AccordionProps) {
  return (
    <Disclosure>
      {({ open }) => (
        <>
          <Disclosure.Button
            as={'div'}
            className="flex items-center justify-center w-full h-12 px-5 py-3 bg-gray-100 cursor-pointer group focus:outline-none focus-visible:ring focus-visible:ring-gray-500 focus-visible:ring-opacity-75 hover:text-gray-800"
          >
            {open ? <BiExpand className="w-4 h-4" /> : <BiCollapse className="w-4 h-4" />}
            <span className="ml-2 select-none">{title}</span>
          </Disclosure.Button>

          <Transition
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-25 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            {/*
                Using `static`, `Disclosure.Panel` is always rendered and
                ignores the `open` state.
              */}
            <Disclosure.Panel className="border-b-2 divide-y-2" static>
              {children}
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
}

interface ActionsDropdownProps {
	request: RequestDto;
	actions: Action[];
}

function ActionsDropdown({ request, actions }: ActionsDropdownProps) {
  return (
    <Menu as="div" className="relative ml-3">
      <div>
        <Menu.Button className="text-gray-800" data-cy="actions-dropdown">
          <FaEllipsisH className="w-5 h-5 " />
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 z-20 w-48 py-1 mt-2 origin-top-left bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {actions.map((action) => (
            <Menu.Item key={action.label}>
              {({ active }) => (
                <button
                  onClick={() => action.onClick(request)}
                  className={clsx(
                    active ? 'bg-gray-100' : '',
                    'flex px-4 py-2 text-sm text-gray-700 w-full items-center justify-between'
                  )}
                  data-cy={getTestSelector(action.label, 'actions-dropdown')}
                >
                  <p>{action.label}</p>
                  {action.svg}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

interface RequestRowProps {
	request: RequestDto;
	actions: Action[];
}

function RequestRow({ request, actions }: RequestRowProps) {
  const { data } = useGetTemplates();
  const { data: workflowList } = useGetWorkflows();
  
  const workflow = workflowList?.find((workflow) => workflow.id === request?.workflowId);
  const templateInfo = data?.find((template) => template.workflowId === workflow?.id);
  
  const shownActions = actions.filter((x) => !x.show || x.show(request));

  return (
    <tr data-cy="request-row">
      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
        <ActionsDropdown request={request} actions={shownActions} />
      </td>
      <td>
        {request.isHighPriority ? (
          <FaExclamation className="w-4 h-4 text-red-400" title="This request is high priority" />
        ) : (
          ''
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="">
            <div className="text-sm font-medium text-gray-900" data-cy="request-row-patient-info">
              {`${request.patient.age} y/o ${request.patient.sexConfig.value}`}
            </div>
            <div className="text-sm text-gray-500" data-cy="request-row-internal-id">
              {request.displayInternalId}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900" data-cy="request-row-template-name">
          {templateInfo?.name}
        </div>
        <div className="text-sm text-gray-500" data-cy="request-row-created-date">
          {request.createdDate && new Date(request.createdDate).toLocaleDateString()}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <RequestStatusPill
          status={request.status}
          statusDisplayNameOverride={{ 'Pending Request': 'Not Submitted' }}
          testSelector="request-row-status"
        />
      </td>
    </tr>
  );
}

function MobileRequestRow({ request, actions }: RequestRowProps) {
  const { data } = useGetTemplates();
  const { data: workflowList } = useGetWorkflows();

  const workflow = workflowList?.find((workflow) => workflow.id === request?.workflowId);
  const templateInfo = data?.find((template) => template.workflowId === workflow?.id);
  
  const shownActions = actions.filter((x) => !x.show || x.show(request));

  return (
    <div className="flex items-center justify-center" key={request.id}>
      <div className="w-full max-w-sm bg-white border border-gray-200 rounded-md shadow-md ">
        <ActionAccordion title="Actions">
          {shownActions.map((action) => (
            <div key={action.label} className="w-full h-12 ">
              <button
                onClick={() => action.onClick(request)}
                className="flex items-center justify-between w-full h-full px-6 hover:bg-gray-50 hover:text-gray-700"
              >
                <p>{action.label}</p>
                {action.svg}
              </button>
            </div>
          ))}
        </ActionAccordion>
        <div className="w-full p-4 space-y-2">
          <div className="flex items-center justify-start h-8">
            <RequestStatusPill
              status={request.status}
              statusDisplayNameOverride={{ 'Pending Request': 'Not Submitted' }}
            />
            <div className={request.isHighPriority ? 'pl-3' : ''}>
              <div className="px-1 py-4 text-sm text-gray-500 whitespace-nowrap">
                {request.isHighPriority ? (
                  <FaExclamation
                    className="w-4 h-4 text-red-400"
                    title="This request is high priority"
                  />
                ) : (
                  ''
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="">
              <div className="py-1 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
								Case
              </div>
              <div className="text-sm text-gray-900">{`${request.patient.age} y/o ${request.patient.sexConfig.value}`}</div>
              <div className="text-sm text-gray-500">{request.displayInternalId}</div>
            </div>
            <div className="">
              <div className="py-1 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
								Submitted to
              </div>
              <div className="text-sm text-gray-900">{templateInfo?.name}</div>
              <div className="text-sm text-gray-500">
                {request.createdDate && new Date(request.createdDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { Requests };
